import { map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import { LOADING_STATE, SIZES } from 'components/AddToCartModal/constants';
import Carousel from 'components/Carousel';
import Colorway from 'components/CustomCollectionProductCard/Colorway';
import { TertiaryHeading } from 'components/Headings';
import LoadingState from 'components/LoadingState';
import { mediumUp } from 'constants/mediaQueries';
import CartUpsell from 'models/CartUpsell';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const CAROUSEL_OPTIONS = {
  DESKTOP: { perPage: 4, threshold: 4, draggable: false, loop: false },
  MOBILE: { perPage: 2.2, threshold: 4, draggable: true, loop: false },
};

const AddToCartModalRecommendations = ({
  recommendations,
  analyticsData,
  trackRecommendationsClick,
}) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });

  return (
    <div className="Recommendations__content">
      {recommendations.length > 0 ? (
        <div className="Recommendations">
          <div className="Recommendations__top">
            <TertiaryHeading>{I18n.localize('pdp.you-might-also-like')}</TertiaryHeading>
          </div>
          <Carousel options={isMediumUp ? CAROUSEL_OPTIONS.DESKTOP : CAROUSEL_OPTIONS.MOBILE}>
            {map(recommendations, (recommendation, index) => (
              <div className="Recommendations__product">
                <Colorway
                  image={recommendation?.image}
                  masterName={recommendation?.productName}
                  colorName={recommendation?.colorName}
                  price={recommendation?.price}
                  compareAtPrice={recommendation?.compareAtPrice}
                  url={recommendation?.linkUrl}
                  enableDiscountPriceColor={true}
                  key={`${recommendation?.productName}-${recommendation?.colorName}`}
                  trackNavigation={() =>
                    trackRecommendationsClick({
                      analyticsProperties: {
                        ...analyticsData,
                        carouselCardPosition: index,
                      },
                      slotId: recommendation?.slotId,
                    })
                  }
                />
              </div>
            ))}
          </Carousel>
        </div>
      ) : (
        <div className="Recommendations__loading-state">
          <LoadingState />
        </div>
      )}
      <style jsx global>{`
        .Recommendations__portal .Modal__content {
          padding: ${Theme.get('spacing') * 3}px;
        }

        .Recommendations__heading-free-shipping .FreeShippingIndicator__messaging-container {
          text-align: left;
        }

        .Recommendations__cart-content {
          margin-bottom: ${Theme.get('spacing') * 3}px;
        }

        .Recommendations__heading {
          border-bottom: solid 1px ${Theme.get('colors-black-20')};
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
        }

        .Recommendations__heading-cart-icon,
        .Recommendations__heading-label,
        .Recommendations__heading-free-shipping {
          margin-bottom: ${Theme.get('spacing')}px;
        }

        .Recommendations__heading-free-shipping {
          clear: both;
        }

        .Recommendations__product {
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
        }

        .Recommendations__loading-state {
          width: ${LOADING_STATE.WIDTH_MOBILE}px;
          height: ${LOADING_STATE.HEIGHT}px;
        }

        .Recommendations__loading-state .LoadingState {
          inset: ${LOADING_STATE.INSET_MOBILE}px 0px 0px 0px;
        }

        .Recommendations__top {
          display: inline-block;
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
          width: 100%;
        }

        .Recommendations__top .typography--tertiary-heading {
          font-size: ${Theme.get('font-size-large-px')};
        }

        .Recommendations__product {
          margin: 0 ${Theme.get('spacing')}px;
        }

        .Recommendations__product .Colorway .Sub {
          font-size: ${Theme.get('font-size-small-px')};
          text-wrap: wrap;
        }

        .Recommendations__content .Carousel {
          margin: 0 -${Theme.get('spacing')}px;
        }

        @media ${mediumUp} {
          .Recommendations__portal .AddToCartModalRecommendations.Modal__content {
            width: ${SIZES.MODAL_WIDTH_DESKTOP}px;
            min-height: ${SIZES.MIN_MODAL_HEIGHT_DESKTOP}px;
            max-height: ${SIZES.MAX_MODAL_HEIGHT_DESKTOP}px;
          }

          .Recommendations__heading-cart-icon {
            padding: ${Theme.get('spacing')}px 0 0 ${Theme.get('spacing') * 2}px;
          }

          .Recommendations__heading-cart-icon,
          .Recommendations__heading-label {
            float: left;
          }

          .Recommendations__product-cart-container {
            display: flex;
          }

          .Recommendations__product {
            flex: 1;
          }

          .Recommendations__subtotal {
            flex: 0 0 40%;
          }

          .AddToCartModalRecommendations .Carousel > div:first-of-type {
            width: 100% !important;
          }

          .Recommendations__loading-state {
            width: ${SIZES.MODAL_WIDTH_DESKTOP}px;
            height: ${LOADING_STATE.HEIGHT}px;
          }

          .Recommendations__loading-state .LoadingState {
            inset: ${LOADING_STATE.INSET_DESKTOP}px 0px 0px 0px;
          }
        }
      `}</style>
    </div>
  );
};

AddToCartModalRecommendations.propTypes = {
  analyticsData: PropTypes.shape({}).isRequired,
  trackRecommendationsClick: PropTypes.func.isRequired,
  recommendations: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.instanceOf(CartUpsell), PropTypes.shape({})]).isRequired
  ).isRequired,
};

export default AddToCartModalRecommendations;
